import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../utils/constants';
import { fetchWithAbort } from '../utils/fetchWithAbort';

export function useTopAppProviders(limit = 50) {
  const [apps, setApps] = useState([]);
  const [asOfRound, setAsOfRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchApps = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    const result = await fetchWithAbort(
      `${API_BASE}/top-app-providers?limit=${limit}`,
      { cacheTTL: 10000 }, // Cache for 10 seconds
      signal
    );

    // Check if aborted or unmounted
    if (signal.aborted || !isMountedRef.current) {
      return;
    }

    if (result.ok) {
      setApps(result.data.apps || []);
      setAsOfRound(result.data.asOfRound || null);
    } else if (!result.aborted) {
      setError(result.error || 'Failed to fetch top app providers');
      console.error('Error fetching top app providers:', result.error);
    }

    setLoading(false);
  }, [limit]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchApps();
    const interval = setInterval(fetchApps, 60000);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchApps]);

  return { apps, asOfRound, loading, error, refetch: fetchApps };
}
